import { useContext, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PageContext from "./PageContext";
import { HttpContext } from '../../api-services/HttpService';
import ApiRequestButton from './ApiRequestButton';
import DynamicPageInput from './DynamicPageInputCopy.jsx';
import applyFormat from '../functions/applyFormat';
import validateInputArray from '../functions/validateInputArray';
import dynamicTransformation from '../functions/dynamicTransformation';
import dynamicValidation from '../functions/dynamicValidation';
import CCOffcanvas from './CCOffcanvas.tsx';
import ButtonComponent from './ButtonComponent.jsx';
import isNullOrUndefined from '../functions/isNullOrUndefined.js'; 

const EditRebateSetupModal = () =>{
    // get context from parent
    const {
        editRebateSetupModal, 
        setEditRebateSetupModal, 
        editRebateSetupRow, 
        masterCompanyData,
        getRefreshData,  
        snackbarAlert, 
        rebateInputTypeId, 
        rebateSetupConfig, 
        rebateSetupLists, 
    } = useContext(PageContext);
    // initialize http context 
    const {
        POST, 
        PUT, 
    } = useContext(HttpContext);
    // get persistant redux global state from store
    const company = useSelector((state) => state.company);
    // initialize state variables
    const [masterSetupBool, setMasterSetupBool] = useState(false);
    const [statusChange, setStatusChange] = useState(false);
    const [values, setValues] = useState({}); 
    const [config, setConfig] = useState([]); 
    const [error, setError] = useState([{}]);

    // event handler for values change 
    const onChange = () => {
        let temp = values;
        // determine if status has changed 
        if (values['active']['id'].toString() !== editRebateSetupRow['active'].toString()) {
            setStatusChange(statusChange => statusChange = true); 
        }
        else {
            setStatusChange(statusChange => statusChange = false); 
        } 
        // set state for filter
        setMasterSetupBool(masterSetupBool => masterSetupBool = temp['mastersetupbool']); 
        // if adding master setup bool make rate and master setup id null
        if (temp['mastersetupbool'] === true){
            temp['incentiverate'] = null; 
            temp['masterincentivesetupid'] = null; 
        } 
        // set inputs to null if they are empty strings
        if (temp['masterincentivesetupid'] != null && temp['masterincentivesetupid'].length == 0){
            temp['masterincentivesetupid'] = null; 
        }  
        if (temp['incentiverate'] != null && temp['incentiverate'].length == 0){
            temp['incentiverate'] = null;
        }
        // set values 
        setValues(values => values = temp); 
    }

    // event handler for submit click
    const onSubmitClick = () =>{       
        return new Promise((resolve, reject) => {
            // make post request if no status change
            if (!statusChange){
                let requestValues = [{...values}]; 
                let dynamicArgs = {
                    incentiverate: {
                        rateTypeId: rebateInputTypeId,
                    } 
                }
                // validate input
                let checkError = validateInputArray(
                    requestValues, 
                    rebateSetupConfig,
                    dynamicValidation,
                    {
                        ...rebateSetupLists, 
                        companyid: {
                            override: false, 
                            list: rebateSetupLists['companyid'] 
                        } 
                    }, 
                    snackbarAlert,  
                    error, 
                    setError, 
                    dynamicTransformation, 
                    dynamicArgs
                ); 
                // exit request if error 
                if (checkError) {
                    reject(); 
                    return; 
                }
                // add mastercompanyid to request body 
                requestValues[0]['mastercompanyid'] = masterCompanyData['mastercompanyid'];
                // get oldValues to attach to request 
                let temp = {...editRebateSetupRow}
                let oldValues = applyFormat(temp, rebateSetupConfig, rebateSetupLists, rebateInputTypeId); 
                oldValues = dynamicTransformation(oldValues, rebateSetupConfig, dynamicArgs); 
                // make request 
                let response; 
                PUT('/client/incentiveSetups', {
                    ...requestValues[0], 
                    oldValues: oldValues
                }).then(res => {
                    console.log(res);
                    response = res; 
                    // hide modal 
                    setEditRebateSetupModal(editRebateSetupModal => editRebateSetupModal = false);
                    // trigger success alert
                    snackbarAlert(true, 'Successfully uploaded data.', 'success');
                    // refresh CompanyDetailsPage using function from parent
                    return getRefreshData();
                }).then(res => {
                    console.log(res);
                    // resolve on success
                    resolve(response); 
                }).catch(err => {
                    console.log(err);
                    // show alert on error
                    snackbarAlert(true, 'Error uploading data.', 'error');
                    // reject on error
                    reject(err);
                });
            }
            // make put request if there is a status change
            else {
                // validate status input 
                if (!rebateSetupLists['active'].includes(values['active'])){
                    // show snackbar on failure
                    snackbarAlert(true, 'Invalid status input.', 'error'); 
                    reject(); 
                    return; 
                }
                // make request 
                let response; 
                PUT('/client/incentiveSetupStatus',  {
                    active: values['active']['id'], 
                    incentivesetupid: values['incentivesetupid'], 
                    mastercompanyid: masterCompanyData['mastercompanyid'], 
                    companyid: values['companyid'], 
                    oldValues: {
                        active: editRebateSetupRow['active']
                    }
                }).then(res => {
                    console.log(res);
                    response = res; 
                    // hide modal 
                    setEditRebateSetupModal(editRebateSetupModal => editRebateSetupModal = false);
                    // trigger success alert
                    snackbarAlert(true, 'Successfully uploaded data.', 'success');
                    // refresh CompanyDetailsPage using function from parent
                    return getRefreshData(company);
                }).then(res => {
                    console.log(res);
                    // resolve on success
                    resolve(response); 
                }).catch(err => {
                    console.log(err);
                    // show alert on error
                    snackbarAlert(true, 'Error uploading data.', 'error');
                    // reject on error
                    reject(err);
                });
            }
        });    
    }

    // useEffect to run on config change 
    useEffect(() => {
        if (rebateSetupConfig){
            setConfig(config => config = rebateSetupConfig); 
        }
    }, [rebateSetupConfig]); 

    // useEffect to run on editRebateSetupRow change
    useEffect(() => {
        if (editRebateSetupRow) {
            // add ids to lookup values 
            let temp = {
                ...editRebateSetupRow // destructure to pass by value 
            }; 
            temp = applyFormat(temp, rebateSetupConfig, rebateSetupLists, rebateInputTypeId); 
            // check for master setup 
            let temp2 = false;
            if (isNullOrUndefined(editRebateSetupRow['masterincentivesetupid']) && isNullOrUndefined(editRebateSetupRow['incentiverate'])){
                temp2 = true; 
                temp['mastersetupbool'] = true; 
            }
            // set state 
            setValues(values => values = temp); 
            setMasterSetupBool(masterSetupBool => masterSetupBool = temp2); 
        }
    }, [editRebateSetupRow]); 

    // use effect to add/remove incentiverate on mastersetup bool change 
    useEffect(() => {
        let temp = {...values}; 
        if (masterSetupBool) {
            temp['incentiverate'] = null; 
        }
        else {
            temp['incentiverate'] = editRebateSetupRow['incentiverate']; 
            temp = applyFormat(temp, rebateSetupConfig, rebateSetupLists, rebateInputTypeId); 
        }
        setValues(values => values = temp); 
    }, [masterSetupBool]); 

    // initialize statusChange on show modal 
    useEffect(() => {
        setStatusChange(statusChange => statusChange = false); 
    }, [editRebateSetupModal]); 

    return(
        <> 
            <CCOffcanvas
                title={ masterSetupBool ?
                    <>Edit Master Setup ID: {editRebateSetupRow['incentivesetupid']}</>
                    :<>Edit Setup ID: {editRebateSetupRow['incentivesetupid']}</>}
                show={editRebateSetupModal}
                onCanvasClose={()=>setEditRebateSetupModal(editRebateSetupModal => editRebateSetupModal = false)}
                bodyCssClassName="offcanvas-body bg-white p-3 pt-2"
                offCanvasClassName="offcanvas-parent-card-30"
                >
                    <div className="d-flex flex-column h-100">
                        <div className="d-flex flex-column gap-3 flex-grow-1 overflow-scroll">
                            {/* map DynamicPageInput over all elements of input config */}
                            {config.map((el) => 
                                el['edit'] == true &&
                                ((!statusChange || el['columnid'] == 'active') && ((el['columnid'] != 'incentiverate' && el['columnid'] != 'masterincentivesetupid') || !masterSetupBool)) ? 
                                <>
                                    <div>
                                        <DynamicPageInput 
                                            data={values} 
                                            setData={setValues} 
                                            config={el} 
                                            args={rebateSetupLists} 
                                            width={'100'} 
                                            onChangeFunction={onChange} 
                                            showError={error[0][el['columnid']]}
                                            topAlignTypography={true}
                                        />
                                    </div>
                                </>
                                : <></>
                            )}
                        </div>
                        {/* button to submit edits to rebate setup */}
                        <div
                            className="d-flex justify-content-end p-3 w-100 offcanvas-footer z-2" style={{height: '65px'}}
                        >
                            <ButtonComponent tag={"Cancel"} 
                                border={'#fcd800 1px solid'} background={'#fff'} color={'#001317'} 
                                onButtonClick={()=>setEditRebateSetupModal(editRebateSetupModal => editRebateSetupModal = false)}
                            />
                            <ApiRequestButton onButtonClick={onSubmitClick} />  
                        </div>
                    </div>
            </CCOffcanvas>
        </>
    );
}

export default EditRebateSetupModal;